import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  saveSignupCredentials,
  setOnboardingComplete,
  socialLoginThunk,
} from '../redux/slices/authSlice';
import SignUpImage from '../assets/Signup.svg';
import GoogleImage from '../assets/Google.svg';
import FacebookImage from '../assets/Facebook.svg';
import AppleImage from '../assets/Apple.svg';
import Logo from '../assets/Logo.svg';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useCountry } from '../helpers/useCountry';
import { signInWithPopup } from 'firebase/auth';
import {
  auth,
  googleProvider,
  // facebookProvider,
  appleProvider,
} from '../firebase';
import {
  containerAnim,
  illustrationAnim,
  staggerFadeUp,
} from '../helpers/animation';
import { validatePassword } from '../helpers/passwordPolicy';
import FooterAuth from '../components/layout/FooterAuth';
export default function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { country, region } = useCountry();
  const [socialLoading, setSocialLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    const email = e.target.email.value.trim().toLowerCase();
    const password = e.target.password.value.trim();

    // --- Combined missing-field validation ---
    let message = '';
    if (!email && !password) {
      message = 'Email and password are required.';
    } else if (!email) {
      message = 'Email is required.';
    } else if (!password) {
      message = 'Password is required.';
    }

    if (message) {
      toast.error(message);
      return;
    }

    // --- Password strength validation ---
    const validationError = validatePassword(password);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // --- Continue as normal ---
    dispatch(saveSignupCredentials({ email, password }));
    navigate('/onboarding');
  };

  const handleSocialSignup = async (providerName) => {
    if (socialLoading) return;
    setSocialLoading(true);

    try {
      let provider;
      switch (providerName) {
        case 'Google':
          provider = googleProvider;
          break;
        // case 'Facebook':
        //   provider = facebookProvider;
        //   break;
        case 'Apple':
          provider = appleProvider;
          break;
        default:
          toast.error('Unsupported provider');
          return;
      }

      localStorage.setItem('authProvider', providerName.toLowerCase());

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();

      await dispatch(
        socialLoginThunk({
          provider: providerName.toLowerCase(),
          idToken,
          country,
          session_state: region,
        })
      ).unwrap();

      dispatch(setOnboardingComplete());
      toast.success(`Welcome ${user.displayName || 'user'} 🎉`);
      navigate('/app');
    } catch (error) {
      console.error(`${providerName} signup error:`, error);
      if (error.code === 'auth/popup-closed-by-user') {
        toast('Signup canceled by user', { icon: '⚠️' });
      } else {
        toast.error(`${providerName} signup failed`);
      }
    } finally {
      setSocialLoading(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col min-h-screen w-full font-sans bg-gray-50 overflow-x-hidden"
      >
        {socialLoading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center"
            >
              <div className="loader border-t-4 border-brand border-solid rounded-full w-12 h-12 animate-spin mb-3"></div>
              <p className="font-semibold text-gray-700">
                Connecting to your account...
              </p>
            </motion.div>
          </div>
        )}
        <div className="flex flex-col md:flex-row flex-grow">
          <motion.div
            {...containerAnim}
            className="w-full md:w-1/2 flex flex-col justify-between px-8 md:px-20 py-12 bg-white shadow-lg relative"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="absolute top-6 left-0 right-0 mx-auto w-fit md:left-10 md:right-auto md:mx-0"
            >
              <a href="/">
                <img
                  src={Logo}
                  alt="Legal Intelligence Logo"
                  className="w-20 h-auto"
                />
              </a>
            </motion.div>

            <div className="w-full max-w-md md:pt-16 mx-auto">
              <motion.h1
                {...staggerFadeUp(0.3)}
                className="font-dmsans text-4xl font-bold text-gray-900 mb-3 text-center mt-8 md:mt-0"
              >
                Sign Up
              </motion.h1>

              <motion.p
                {...staggerFadeUp(0.4)}
                className="text-md mb-8 text-center font-bold"
              >
                Start your legal AI experience.
              </motion.p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-brand focus:outline-none"
                />
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Password"
                    className="w-full border border-gray-300 rounded px-4 py-2 pr-10 focus:ring-2 focus:ring-brand focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    title={showPassword ? 'Hide Password' : 'Show Password'}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-gradient-to-r from-brandDark to-brand text-white py-2 rounded hover:bg-brand transition"
                >
                  Sign Up
                </motion.button>
              </form>

              <div className="my-5 flex items-center">
                <div className="flex-grow border-t border-brand"></div>
                <span className="mx-3 text-sm text-gray-500">or</span>
                <div className="flex-grow border-t border-brand"></div>
              </div>

              <div className="space-y-3">
                {['Google', 'Apple'].map((provider) => (
                  <motion.button
                    key={provider}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSocialSignup(provider)}
                    disabled={socialLoading}
                    className="w-full flex items-center justify-center border border-brand py-2 rounded text-brand hover:bg-gray-100 transition"
                  >
                    <img
                      src={
                        provider === 'Google'
                          ? GoogleImage
                          : // : provider === 'Facebook'
                            // ? FacebookImage
                            AppleImage
                      }
                      alt={provider}
                      className="w-6 h-6 mr-2"
                    />
                    Sign up with {provider}
                  </motion.button>
                ))}
              </div>

              <p className="mt-4 text-sm text-gray-600 text-center">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-black underline font-semibold hover:underline"
                >
                  Log In
                </Link>
              </p>
            </div>
          </motion.div>

          <motion.div
            {...illustrationAnim}
            className="w-full md:w-1/2 flex items-center justify-center bg-[#DADDFB]"
          >
            <img
              src={SignUpImage}
              alt="Sign Up Illustration"
              className="max-w-[80%] max-h-[80%] object-contain"
              loading="lazy"
            />
          </motion.div>
        </div>
      </motion.div>
      <FooterAuth />
    </>
  );
}
